import { Button, Card, Text } from "@astryxdesign/core";
import * as stylex from "@stylexjs/stylex";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { AppShell } from "@/components/app-shell";
import { api } from "@/lib/api";
import { useSession } from "@/services/auth0/session";
import type { Trip } from "@/types/api";

type Status = "COLLECTED" | "MISSING";
type ShipmentRow = {
  id: string;
  orderId: string;
  shipmentNumber: number;
  orderNumber: number;
  packageCount: number;
};
type PickupGroup = {
  operator: { id: string; name: string | null } | null;
  senderGroups: { senderName: string; shipments: ShipmentRow[] }[];
};
type CollectionReference = { operators: PickupGroup[] };
type Checklist = {
  items: { shipmentId: string; status: Status }[];
  legacyItems: { orderId: string; orderNumber: number; status: Status }[];
};

const styles = stylex.create({
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    marginBottom: 14,
    color: "var(--color-text-secondary)",
    textDecoration: "none",
  },
  stack: { display: "grid", gap: 14 },
  titleBlock: { display: "grid", gap: 6 },
  group: { display: "grid", gap: 10 },
  shipmentReference: { display: "grid", gap: 2 },
  heading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    borderTop: "1px solid var(--color-border)",
    paddingBlock: 9,
  },
  choice: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    marginRight: 12,
  },
  actions: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
  error: { color: "var(--color-error)" },
  warning: {
    border: "1px solid var(--color-border)",
    padding: 12,
    borderRadius: 8,
  },
  sheet: { display: "grid", gap: 15 },
});

function CollectionForm({
  trip,
  reference,
  checklist,
}: {
  trip: Trip;
  reference: CollectionReference;
  checklist: Checklist;
}) {
  const queryClient = useQueryClient();
  const initialStatuses = useMemo(
    () =>
      new Map(checklist.items.map((item) => [item.shipmentId, item.status])),
    [checklist.items]
  );
  const [statuses, setStatuses] = useState<Record<string, Status | undefined>>(
    () => Object.fromEntries(initialStatuses)
  );
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const rows = reference.operators.flatMap((group) =>
    group.senderGroups.flatMap((sender) => sender.shipments)
  );
  const blockedOrderIds = new Set(
    checklist.legacyItems.map((item) => item.orderId)
  );
  const actionableRows = rows.filter(
    (row) => !blockedOrderIds.has(row.orderId)
  );
  const missing = actionableRows.filter(
    (row) => statuses[row.id] === "MISSING"
  );
  const allResolved =
    actionableRows.length > 0 &&
    actionableRows.every((row) => statuses[row.id]);
  const save = useMutation({
    mutationFn: () =>
      api.post(`/api/v2/trips/${trip.id}/shipment-pickup-checklist`, {
        items: actionableRows.map((row) => ({
          shipmentId: row.id,
          status: statuses[row.id],
        })),
      }),
    onSuccess: () => {
      setReviewing(false);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["collection", trip.id] });
      void queryClient.invalidateQueries({ queryKey: ["trip", trip.id] });
      void queryClient.invalidateQueries({ queryKey: ["operator-trips"] });
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof Error ? cause.message : "Could not save collection"
      );
    },
  });

  return (
    <div {...stylex.props(styles.stack)}>
      {rows.length === 0 ? (
        <Text size="sm" color="secondary">
          No delivery shipments on this trip.
        </Text>
      ) : null}
      <div className="collection-print-page">
        <div {...stylex.props(styles.sheet)}>
          <div {...stylex.props(styles.titleBlock)}>
            <Text as="h1" size="lg" weight="semibold">
              Trip collection ·{" "}
              {trip.tripNumber != null
                ? `#${trip.tripNumber}`
                : (trip.name ?? trip.id)}
            </Text>
            <Text size="sm" color="secondary">
              Picked up by shows who completed pickup; it is not a current
              location.
            </Text>
          </div>
          {checklist.legacyItems.length > 0 ? (
            <div role="status" {...stylex.props(styles.warning)}>
              Earlier Order-level results need dispatch review before Shipment
              confirmation:
              <ul>
                {checklist.legacyItems.map((item) => (
                  <li key={item.orderId}>
                    ORD-{item.orderNumber}: {item.status.toLowerCase()} ·
                    Shipment unresolved
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {reference.operators.map((group) => (
            <Card key={group.operator?.id ?? "unknown"} padding={4}>
              <div {...stylex.props(styles.group)}>
                <Text weight="semibold">
                  Picked up by · {group.operator?.name ?? "Pickup not recorded"}
                </Text>
                {group.senderGroups.map((sender) => (
                  <div key={sender.senderName}>
                    <Text size="sm" color="secondary">
                      Sender · {sender.senderName}
                    </Text>
                    {sender.shipments.map((shipment) => (
                      <div key={shipment.id} {...stylex.props(styles.row)}>
                        <div {...stylex.props(styles.shipmentReference)}>
                          <Text weight="medium">
                            SHP-{shipment.shipmentNumber}
                          </Text>
                          <Text size="sm" color="secondary">
                            ORD-{shipment.orderNumber} · {shipment.packageCount}{" "}
                            package{shipment.packageCount === 1 ? "" : "s"}
                          </Text>
                        </div>
                        <Text size="sm" color="secondary">
                          Saved: {initialStatuses.get(shipment.id) ?? "Pending"}
                        </Text>
                        {blockedOrderIds.has(shipment.orderId) ? (
                          <Text size="sm" color="secondary">
                            Historical Order result needs dispatch review
                          </Text>
                        ) : null}
                        {trip.status === "PLANNED" ? (
                          <fieldset
                            className="collection-print-hidden"
                            aria-label={`Collection status for SHP-${shipment.shipmentNumber}`}
                            style={{ border: 0, padding: 0, margin: 0 }}
                          >
                            {(["COLLECTED", "MISSING"] as const).map(
                              (status) => (
                                <label
                                  key={status}
                                  {...stylex.props(styles.choice)}
                                >
                                  <input
                                    type="radio"
                                    name={`shipment-${shipment.id}`}
                                    checked={statuses[shipment.id] === status}
                                    disabled={
                                      blockedOrderIds.has(shipment.orderId) ||
                                      initialStatuses.get(shipment.id) ===
                                        "MISSING" ||
                                      save.isPending
                                    }
                                    onChange={() => {
                                      setStatuses((current) => ({
                                        ...current,
                                        [shipment.id]: status,
                                      }));
                                      setReviewing(false);
                                      setSaved(false);
                                    }}
                                  />
                                  {status === "COLLECTED"
                                    ? "Collected"
                                    : "Missing"}
                                </label>
                              )
                            )}
                          </fieldset>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
      <div className="collection-print-hidden">
        <div {...stylex.props(styles.actions)}>
          <Button
            variant="secondary"
            icon={<Printer size={16} />}
            label="Print collection list"
            onClick={() => window.print()}
          />
          {trip.status === "PLANNED" && actionableRows.length > 0 ? (
            <Button
              variant="primary"
              label="Review collection"
              isDisabled={!allResolved || save.isPending}
              onClick={() => {
                setError(null);
                setReviewing(true);
              }}
            />
          ) : null}
        </div>
      </div>
      {reviewing ? (
        <div className="collection-print-hidden">
          <div {...stylex.props(styles.warning)}>
            <Text weight="semibold">Review before saving</Text>
            <Text size="sm">
              {missing.length > 0
                ? `Missing: ${missing.map((row) => `SHP-${row.shipmentNumber}`).join(", ")}. Their eligible delivery work may be failed.`
                : "All listed shipments are collected."}
            </Text>
            <div {...stylex.props(styles.actions)}>
              <Button
                variant="secondary"
                label="Back"
                onClick={() => setReviewing(false)}
              />
              <Button
                variant="primary"
                label="Confirm collection"
                isLoading={save.isPending}
                isDisabled={save.isPending}
                onClick={() => save.mutate()}
              />
            </div>
          </div>
        </div>
      ) : null}
      {error ? (
        <span role="alert" {...stylex.props(styles.error)}>
          {error}
        </span>
      ) : null}
      {saved ? <span role="status">Collection saved</span> : null}
    </div>
  );
}

export function CollectionScreen() {
  const { tripId } = useParams<{ tripId: string }>();
  const { operatorId } = useSession();
  const trip = useQuery({
    queryKey: ["trip", tripId],
    queryFn: async () => (await api.get<Trip>(`/api/v2/trips/${tripId}`)).data,
    enabled: !!tripId && !!operatorId,
  });
  const reference = useQuery({
    queryKey: ["collection", tripId, operatorId, "reference"],
    queryFn: async () =>
      (
        await api.get<CollectionReference>(
          `/api/v2/trips/${tripId}/shipment-pickup-operators`
        )
      ).data,
    enabled: !!tripId && !!operatorId,
  });
  const checklist = useQuery({
    queryKey: ["collection", tripId, operatorId, "status"],
    queryFn: async () =>
      (
        await api.get<Checklist>(
          `/api/v2/trips/${tripId}/shipment-pickup-checklist`
        )
      ).data,
    enabled: !!tripId && !!operatorId,
  });

  return (
    <AppShell>
      <div className="collection-print-hidden">
        <Link to={`/trips/${tripId}`} {...stylex.props(styles.back)}>
          <ArrowLeft size={16} /> Trip detail
        </Link>
      </div>
      {trip.isLoading || reference.isLoading || checklist.isLoading ? (
        <Text size="sm">Loading collection…</Text>
      ) : null}
      {trip.isError || reference.isError || checklist.isError ? (
        <span role="alert" {...stylex.props(styles.error)}>
          Could not load collection. Refresh to try again.
        </span>
      ) : null}
      {trip.data && reference.data && checklist.data ? (
        <CollectionForm
          key={`${operatorId}:${trip.data.id}`}
          trip={trip.data}
          reference={reference.data}
          checklist={checklist.data}
        />
      ) : null}
    </AppShell>
  );
}
